'use client';

import { cn } from '@/lib/utils';
import Image from '@/helpers/Image';

import checkDark from '@/public/text-only-crypto/check.svg';
import checkLight from '@/public/text-only-crypto/check-light.svg';

/**
 * `paddingClassName` replaces the default padding instead of merging into it:
 * tailwind-merge drops a bare `p-[..]` but keeps `lg:px-[4rem]`, so padding
 * sent through `panelClassName` leaks on desktop.
 */
export default function CheckList({
  items,
  tone = 'light',
  checkAlign = 'center',
  paddingClassName,
  panelClassName,
  textClassName,
  className,
}: {
  items?: string[];
  tone?: 'light' | 'dark';
  checkAlign?: 'center' | 'top';
  paddingClassName?: string;
  panelClassName?: string;
  textClassName?: string;
  className?: string;
}) {
  if (!items || items.length === 0) return null;
  const isDark = tone === 'dark';
  const isTop = checkAlign === 'top';

  return (
    <div
      className={cn(
        'flex flex-col w-full border',
        isDark
          ? 'bg-white/5 border-white/10'
          : 'border-[#001B14]/[0.08] rounded-[0.75rem]',
        paddingClassName ??
          (isDark
            ? 'max-lg:p-[2rem] lg:p-[3rem]'
            : 'max-lg:p-[2rem] lg:px-[4rem] lg:py-[3.5rem]'),
        panelClassName,
        className,
      )}
    >
      <ul className="flex flex-col gap-[2.25rem] list-none m-0 p-0">
        {items.map((item, index) => (
          <li
            key={`${index}-${item}`}
            className={cn(
              'flex',
              isTop
                ? 'items-start max-lg:flex-col max-lg:gap-[1.5rem] lg:gap-[2rem]'
                : 'items-center gap-[1.5rem]',
            )}
          >
            <Image
              src={isDark ? checkLight : checkDark}
              alt=""
              className="w-[3.625rem] h-[3.625rem] shrink-0"
            />
            <p
              className={cn(
                'mb-0 font-geist text-[2.25rem] leading-normal',
                isDark
                  ? 'font-normal text-white opacity-70'
                  : 'font-medium text-[#001B14]/[0.88]',
                isTop &&
                  'lg:[text-box-trim:trim-both] lg:[text-box-edge:cap_alphabetic]',
                textClassName,
              )}
            >
              {item}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
